import { type FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../lib/auth';

export function Login(): JSX.Element {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (auth.status === 'signed-in') {
    // Match App.tsx's "/" and catch-all redirects — Aurora-free landing page so
    // a fresh sign-in doesn't wake the cluster.
    return <Navigate to="/digests" replace />;
  }

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (auth.status === 'force-change-password') {
        await auth.completeNewPassword(newPassword);
      } else {
        await auth.signIn(email, password);
      }
    } catch {
      // useAuth has already set state.error.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="card w-full max-w-sm p-6">
        <h1 className="wordmark text-base mb-1">
          sjramblings <span className="wordmark-accent">/feed</span>
        </h1>
        <p className="text-2xs font-mono text-ink-muted uppercase tracking-wider mb-6">
          single-user admin · cognito · srp
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          {auth.status === 'force-change-password' ? (
            <Field
              label="New password"
              type="password"
              value={newPassword}
              onChange={setNewPassword}
              autoFocus
              autoComplete="new-password"
              minLength={12}
            />
          ) : (
            <>
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                autoFocus
                autoComplete="username"
              />
              <Field
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                minLength={12}
              />
            </>
          )}

          {auth.error !== null && (
            <p className="text-2xs font-mono text-bad/90 border border-bad/30 bg-bad/10 px-3 py-2 rounded">
              {auth.error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-accent hover:bg-accent-hover text-white text-sm font-mono uppercase tracking-wider py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? '…' : auth.status === 'force-change-password' ? 'Set password' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

interface FieldProps {
  readonly label: string;
  readonly type: 'email' | 'password';
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly autoFocus?: boolean;
  readonly autoComplete?: string;
  readonly minLength?: number;
}

function Field(props: FieldProps): JSX.Element {
  return (
    <label className="block">
      <span className="text-2xs font-mono text-ink-dim uppercase tracking-wider">
        {props.label}
      </span>
      <input
        type={props.type}
        value={props.value}
        onChange={(e) => { props.onChange(e.target.value); }}
        autoFocus={props.autoFocus}
        autoComplete={props.autoComplete}
        minLength={props.minLength}
        required
        className="mt-1 block w-full bg-bg-base border border-bg-line text-ink rounded px-3 py-2 font-mono text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
